import { useAnchor, useAuth } from '@checkout/composables';
import { STEPS, steps } from '@checkout/constants';
import type { StepKey, StepState } from '@checkout/models';
import { computed, onMounted, reactive } from '@nuxtjs/composition-api';

import { validateStep } from '../../helpers/validateStep';
import { useCheckoutStore } from '../../stores';

export const useCheckoutStep = () => {
  const { activeAnchor, setAnchor } = useAnchor();
  const { isAuthenticated } = useAuth();
  const checkoutStore = useCheckoutStore();
  const DEFAULT_STEP: StepKey = STEPS.USER_ACCOUNT;
  const currentStep = computed(() => (validateStep(activeAnchor.value) ? (activeAnchor.value as StepKey) : DEFAULT_STEP));

  const defaultStepState: StepState = {
    expanded: false,
    disabled: true,
    static: false
  };
  const state = reactive<Record<StepKey, StepState>>({
    [STEPS.USER_ACCOUNT]: { ...defaultStepState },
    [STEPS.SHIPPING]: { ...defaultStepState },
    [STEPS.PAYMENT]: { ...defaultStepState }
  });

  const updateStepState = (key: StepKey, { expanded, disabled, isStatic }: Omit<StepState, 'static'> & { isStatic: boolean }) => {
    state[key].expanded = expanded;
    state[key].disabled = disabled;
    state[key].static = isStatic;
  };

  const openStep = (key: StepKey) => {
    updateStepState(key, { expanded: true, disabled: false, isStatic: false });
    setAnchor(key);
  };

  const completeStep = (key: StepKey) => {
    updateStepState(key, { expanded: false, disabled: false, isStatic: true });
  };

  const disableStep = (key: StepKey) => {
    updateStepState(key, { expanded: false, disabled: true, isStatic: false });
  };

  const isStepCompleted = (step: StepKey) => !!checkoutStore.state[step];

  const isLoggedInAccountStep = (step: StepKey) => step === STEPS.USER_ACCOUNT && isAuthenticated.value;

  const proceedToNextStep = (currentIndex: number) => {
    if (currentIndex < 0 || currentIndex >= steps.length - 1) return;

    completeStep(steps[currentIndex]);
    openStep(steps[currentIndex + 1]);
  };

  const handleStepChange = (targetStep: StepKey) => {
    if (isLoggedInAccountStep(targetStep) || currentStep.value === targetStep) return;

    if (isStepCompleted(currentStep.value)) {
      completeStep(currentStep.value);
    } else {
      disableStep(currentStep.value);
    }

    openStep(targetStep);
  };

  const initializeSteps = () => {
    const currentStepIndex = steps.indexOf(currentStep.value);

    // Complete previous steps if they have data
    steps.slice(0, currentStepIndex).forEach((step) => {
      if (isStepCompleted(step)) {
        completeStep(step);
      }
    });

    // Proceed to shipping step if auth step is complete
    if (currentStep.value === DEFAULT_STEP && isStepCompleted(currentStep.value)) {
      proceedToNextStep(0);
      return;
    }

    // Open current step if:
    //  - has data
    //  - previous step is completed
    //  - it is auth step
    const isCurrentStepValid =
      isStepCompleted(currentStep.value) ||
      (currentStepIndex > 0 && isStepCompleted(steps[currentStepIndex - 1])) ||
      currentStep.value === DEFAULT_STEP;

    if (isCurrentStepValid) {
      openStep(currentStep.value);
      return;
    }

    // Redirect to first incomplete step
    const firstIncompleteIndex = steps.findIndex((step) => !isStepCompleted(step));
    openStep(firstIncompleteIndex !== -1 ? steps[firstIncompleteIndex] : steps[steps.length - 1]);
  };

  onMounted(initializeSteps);

  return {
    currentStep,
    openStep,
    disableStep,
    completeStep,
    state,
    proceedToNextStep,
    handleStepChange,
    isLoggedInAccountStep
  };
};
